/* Copyright (c) 2016-2023 Planticle Pty Ltd */

import ko from "knockout";
import birdata from "../../../api.js";
import fieldManager from "../../../util/fields.js";
import util from "../../../util/util.js";
import * as eventUtil from "../../../util/event-util.js";
import SortableTableViewModel from "../../shared/sortable-table.js";
import h from "util/dom-builder.js";


function ManageEventsDetailViewModel(params) {
	var self = this;

	self.event = ko.observable();
	self.program = ko.observable();
	self.dates = ko.observable();
	self.eventPageURL = ko.observable();
	self.stats = ko.pureComputed(function() {
		return self.event() && self.event().stats;
	});
	self.eventStatus = ko.pureComputed(function() {
		var event = self.event();
		if(event) {
			if(event.finished) {
				return "Finished";
			} else {
				var started = util.formatISODate(new Date()) >= event.start_date;
				return (started ? "In progress" : "Event has not started") + ", " +
					(event.bookings_open ? "Bookings open" : "Bookings closed");
			}
		}
	});

	self.bookings = loaderObservable(function() {
		return birdata.eventBookings(params.id);
	});
	self.bookings.refresh();

	self.exportBookings = function() {
		birdata.eventBookingsExportURL(params.id).then(function(url) {
			window.open(url, '_blank');
		});
	}

	self.registrations = loaderObservable(function() {
		return birdata.eventRegistrations(params.id);
	});
	self.registrations.refresh();

	self.exportRegistrations = function() {
		birdata.eventRegistrationsExportURL(params.id).then(function(url) {
			window.open(url, '_blank');
		});
	}

	Promise.all([
		birdata.event(params.id),
		birdata.lookup()])
	.then(function([event, lookup]) {
		self.program(lookup.find('program', event.program));
		self.dates(util.formatDate(event.start_date) + " to " + util.formatDate(event.finish_date));
		self.event(event);
		self.eventPageURL(eventUtil.eventPageURL(event.code));
	}).catch(function(error) {
		console.log('Failed to load event');
	});

	self.bookingTable = new SortableTableViewModel([
		{
			name: 'Volunteer name',
			value: function(b) {
				return b.volunteer;
				// return b.members.map(m => m.full_name).join(", ");
			},
			formatted: function(b) { return this.value(b); },
			id: 'name'
		}, {
			name: 'Site',
			value: function(b) { return b.site; },
			formatted: function(b) { return this.value(b); },
			id: 'site'
		}, {
			name: 'Date',
			value: function(b) { return b.booking_date; },
			formatted: function(b) { return util.formatDate(b.booking_date); },
			id: 'date'
		}, {
			name: 'Booking Code',
			value: function(b) { return b.booking_code; },
			formatted: function(b) { return this.value(b); },
			id: 'code'
		}, {
			name: 'Surveys',
			value: function(b) { return b.surveys_recorded_at_site_during_event; },
			formatted: function(b) { return this.value(b); },
			id: 'surveys'
		}
	]);

	self.bookingTable.sortDescriptors([{
		column: 'site',
		ascending: true
	}]);

	self.sortedBookings = self.bookingTable.sortedItems(self.bookings);


	// Registrations table

	self.registrationTable = new SortableTableViewModel([
		{
			name: 'Volunteer name',
			value: function(r) {
				return r.user.full_name;
			},
			formatted: function(r) { return this.value(r); },
			id: 'name'
		}, {
			name: 'Email',
			value: function(r) { return r.user.email || ''; },
			formatted: function(r) { return this.value(r); },
			id: 'email'
		}, {
			name: 'Registration Date',
			value: function(r) { return r.registration_date; },
			formatted: function(r) { return util.formatDate(r.registration_date); },
			id: 'date'
		}, {
			name: 'Bookings',
			value: function(r) { return r.booking_count; },
			formatted: function(r) { return this.value(r).toLocaleString() },
			id: 'bookings'
		}
	]);

	self.registrationTable.sortDescriptors([{
		column: 'date',
		ascending: false
	}]);

	self.sortedRegistrations = self.registrationTable.sortedItems(self.registrations);

	self.canDeleteEvent = ko.pureComputed(function() {
		var event = self.event();
		return event && event.can_delete;
	});

	self.deleteEvent = function() {
		if(!self.canDeleteEvent()) {
			return;
		}
		showDeleteDialog(function() {
			birdata.deleteEvent(self.event().id).then(function() {
				params.parent.listEvents();
			}).catch(function(error) {
				console.log(error);
				alert('Failed to delete event');
			});
		});
	}

	function showDeleteDialog(confirmCallback) {
		function onClose() {
			dialog.parentElement.removeChild(dialog);

			if(dialog.returnValue === 'confirm' && confirmCallback) {
				confirmCallback();
			}
		}

		var deleteButton = h('button', { 'value': 'confirm', 'class': 'button primary', 'disabled': '' }, 'Delete');
		var confirmationCheckbox = h('input', { type: 'checkbox', $change: function() {
			if(confirmationCheckbox.checked) {
				deleteButton.removeAttribute('disabled');
			} else {
				deleteButton.setAttribute('disabled', '');
			}
		}});

		var dialog = h('dialog', { $close: onClose }, [
			h('form', { 'method': 'dialog' }, [
				h('h1', 'Delete Event'),
				h('p', 'Are you sure you wish to delete this event?'),
				h('p', 'You will no longer be able to access registration or booking information from this event.'),
				h('label', [
					confirmationCheckbox,
					'I understand and still wish to delete this event.'
				]),
				h('div', { 'class': 'buttons' }, [
					h('button', { 'value': 'dismiss', 'class': 'button' }, 'Cancel'),
					deleteButton
				])
			].flat())
		]);

		// Container for styling
		var container = h('div', { 'class': 'birdata-embed' }, dialog);

		window.document.body.appendChild(container);
		dialog.showModal();
	}


	// Utility functions

	function firstly(fn) {
		return new Promise(function(resolve) {
			resolve(fn());
		});
	}

	function loaderObservable(promiseFn) {
		var obs = ko.observable();
		obs.loaded = ko.observable();
		obs.nonEmpty = ko.observable();
		obs.empty = ko.observable();
		obs.error = ko.observable();
		obs.refresh = function() {
			firstly(function() {
				obs.loaded(false);
				obs.nonEmpty(false);
				obs.empty(false);
				return promiseFn();
			}).then(function(val) {
				obs(val);
				obs.nonEmpty(val && val.length);
				obs.empty(!(val && val.length));
			}).catch(function(error) {
				console.log(error);
				obs.error(error);
			}).finally(function() {
				obs.loaded(true);
			});
		};
		return obs;
	}

	self.edit = function() {
		params.parent.editEvent(params.id);
	}
}

ko.components.register('manage-events-detail', {
	viewModel: function(params) {
		return new ManageEventsDetailViewModel(params);
	},
	template: require('../../../../templates/admin/events/manage-events-detail.html')
});
